import _ from 'lodash'

import { ESignature, Image } from '../../../../generated/server-types/entity/definitions/eSignature'
import { EditableFieldProps } from '../core-field/editable-field-json-props'

export const UI2_SIGNATURE_FIELD_DEFAULT_PROPS: Partial<UI2SignatureFieldProps> = {
  immutableSignature: true,
  previewLabel: 'Signature',
  timestampLabel: 'Signed at',
  nameLabel: 'Signed by',
  /** @platform web — mobile always uses the drawing pad; this is the consent-checkbox default. */
  showSignatureCheckbox: true,
}

export interface UI2SignatureFieldProps extends EditableFieldProps<ESignature> {
  entity: any
  /** When true, a captured signature cannot be cleared or replaced. */
  immutableSignature?: boolean
  /** Label shown above the signature preview in read-only display. Default: "Signature". */
  previewLabel?: string
  /** Label shown above the captured timestamp in read-only display. Default: "Signed at". */
  timestampLabel?: string
  /** Label shown above the signer's name in read-only display. Default: "Signed by". */
  nameLabel?: string
  /**
   * Disclosure statement (markdown supported) rendered near the capture
   * affordance. Typical use: legal e-signature consent copy that the
   * signer implicitly agrees to by signing.
   *
   * Only rendered by the consent-checkbox flow, so desktop web only.
   */
  signatureDisclosure?: string
  /**
   * Selects the consent-checkbox capture flow: the user ticks a box to
   * confirm consent, and a cursive rendering of their name is captured as
   * the signature image. When false, the user draws their signature on a
   * pad instead.
   *
   * Desktop web only, where it defaults to `true`. Native mobile and mobile
   * web always use the drawing pad and ignore this prop — a touch device has
   * a real signing surface, so the generated-cursive fallback is unnecessary
   * there.
   */
  showSignatureCheckbox?: boolean
}

// ---------------------------------------------------------------------------
// Value helpers — shared by every platform's implementation
// ---------------------------------------------------------------------------

export interface IBuildSignatureEnvelopeArgs {
  signatureType: ESignature['signatureType']
  /**
   * The captured image descriptor. Pad captures pass the platform's file
   * object; the consent-checkbox flow passes a `{ name, type, uniqueId }`
   * placeholder that the pre-save screenshot later fills in.
   */
  signature: Image
  signerName: string
  signerUserId: string
  signerFirmId: string
  /** ISO-8601. Callers pass trusted time (NM uses TrueTime) rather than the device clock. */
  signedDate: string
}

/**
 * The single value shape every platform writes. Fields bound to this
 * component always store the full envelope — never a bare image object.
 */
export function buildSignatureEnvelope(args: IBuildSignatureEnvelopeArgs): ESignature {
  const { signatureType, signature, signerName, signerUserId, signerFirmId, signedDate } = args
  return {
    signature,
    signatureType,
    signed: true,
    signer: signerName,
    signedDate,
    signedBy: {
      user: { entityId: signerUserId },
      firm: { entityId: signerFirmId },
    },
  }
}

/** Keys that identify a value as a signature envelope rather than a bare image. */
const ENVELOPE_KEYS: Array<keyof ESignature> = [
  'signed',
  'signer',
  'signedDate',
  'signature',
  'signatureType',
  'signedBy',
]

export function isSignatureEmpty(value: ESignature | undefined): boolean {
  return _.isEmpty(value)
}

export function isSignatureSigned(value: ESignature | undefined): boolean {
  return !!_.get(value, 'signed', false)
}

/**
 * Resolves the displayable image URI from an envelope, tolerating the several
 * `source` shapes the capture flows produce: `ESignature.signature.source` is
 * the server's converted `ImageSource[]`, while a pending local capture is a
 * `file://` or object URL sitting directly on `uri` (or on a single-object
 * `source` the native image cache hands back).
 *
 * Also reads the legacy bare-image shape (`{ uri }` / `{ source }` written by
 * the pre-UI2 mobile signature field) so existing data still renders.
 */
export function readSignatureImageUri(value: ESignature | undefined): string | undefined {
  const candidate: any = _.get(value, 'signature') ?? value
  const uri =
    _.get(candidate, 'source[0].uri') ||
    _.get(candidate, 'source.uri') ||
    _.get(candidate, 'uri')
  return _.isString(uri) && !_.isEmpty(uri) ? uri : undefined
}

/**
 * The raw `source` value, for platforms whose image component consumes the
 * descriptor directly (NM's `CachedImage`) rather than a URI string.
 */
export function readSignatureImageSource(value: ESignature | undefined): any {
  const candidate: any = _.get(value, 'signature') ?? value
  const source = _.get(candidate, 'source')
  if (!_.isNil(source)) return source
  const uri = readSignatureImageUri(value)
  return uri ? { uri } : undefined
}

/**
 * Guards against a schema binding this field to a path that holds something
 * other than an `ESignature` envelope — most often a legacy bare-image path
 * from the pre-UI2 `ui:form:signatureField`. Returns false and logs; the field
 * still renders what it can.
 */
export function validateSignatureEnvelope(
  value: ESignature | undefined,
  valuePath?: string,
): boolean {
  if (isSignatureEmpty(value)) return true
  const where = valuePath ? ` at "${valuePath}"` : ''
  if (!_.isPlainObject(value)) {
    console.error(
      `ui2:signatureField — bound value${where} is a ${typeof value}, expected an ESignature object.`,
    )
    return false
  }
  if (!_.some(ENVELOPE_KEYS, (key) => _.has(value, key))) {
    console.error(
      `ui2:signatureField — bound value${where} is not an ESignature envelope ` +
        `(expected one of ${ENVELOPE_KEYS.join(', ')}; got ${_.keys(value).join(', ')}). ` +
        'This field always writes the full envelope — rebind it to an object property, ' +
        'or migrate the stored value.',
    )
    return false
  }
  return true
}
